import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { Copy, ExternalLink, Check, WifiOff } from "lucide-react";
import Button from "@/components/ui/button/Button";
import { getFromLocal, OFFLINE_KEYS } from "@/utils/offlineStorage";
import { OfflineSurveyModal } from "./OfflineSurveyModal";
import { apiFetch } from "@/service/api";
import { getSessionItem } from "@/utils/storage";

interface EncuestaUsuarioProyecto {
  id: number;
  proyecto: {
    id: number;
    nombre: string;
    empresa: { id: number; nombre: string };
  };
  encuesta: { id: number; nombre: string; descripcion: string };
  cantidad: number;
  cantidad_respuestas: number;
  observaciones: string;
}

export default function EncuestasMobile() {
  const { user } = useAuth();
  const [encuestas, setEncuestas] = useState<EncuestaUsuarioProyecto[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [selectedAssignment, setSelectedAssignment] = useState<any>(null);
  const [isOfflineModalOpen, setIsOfflineModalOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const url_survey = import.meta.env.VITE_API_URL_SURVEY;
  const pendingResponses = getFromLocal(OFFLINE_KEYS.PENDING_RESPONSES) || [];

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const fetchEncuestas = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const response = await apiFetch(
        `encuesta-usuario-proyecto/find-by-user/${user.id}`,
      );
      if (response.ok) {
        const data = await response.json();
        setEncuestas(data);
      } else {
        const offlineData = getFromLocal(OFFLINE_KEYS.ENCUESTAS);
        if (offlineData) setEncuestas(offlineData);
      }
    } catch (error) {
      console.error("Error fetching encuestas:", error);
      const offlineData = getFromLocal(OFFLINE_KEYS.ENCUESTAS);
      if (offlineData) setEncuestas(offlineData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEncuestas();
  }, [user?.id]);

  const handleCopyLink = async (encuestaId: number, assignmentId: number) => {
    const link = `${url_survey}${encuestaId}?assignment_id=${assignmentId}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(assignmentId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Error al copiar enlace:", err);
    }
  };

  return (
    <div>
      <PageBreadcrumb pageTitle="Mis Encuestas" />
      <div className="min-h-screen rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
        <h3 className="mb-6 font-semibold text-gray-800 text-center text-theme-xl dark:text-white/90 sm:text-2xl">
          Encuestas Asignadas
        </h3>
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500"></div>
          </div>
        ) : encuestas.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            No tienes encuestas asignadas en este momento.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {encuestas.map((item) => {
              const hasOfflineData = getFromLocal(
                `survey_detail_${item.encuesta?.id}`,
              );
              const pendingOfflineCount = pendingResponses.filter(
                (response: any) =>
                  Number(response.data?.assignment_id) === Number(item.id),
              ).length;
              const savedCount = Number(item.cantidad_respuestas) || 0;
              const assignedCount = Number(item.cantidad) || 0;
              const savedProgress = assignedCount
                ? Math.min((savedCount / assignedCount) * 100, 100)
                : 0;
              const offlineProgress = assignedCount
                ? Math.min(
                    (pendingOfflineCount / assignedCount) * 100,
                    100 - savedProgress,
                  )
                : 0;
              return (
                <div
                  key={item.id}
                  className="rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-800/50"
                >
                  <div className="mb-4">
                    <span className="text-xs font-medium uppercase tracking-wider text-brand-500">
                      {item.proyecto?.empresa?.nombre || "Sin Empresa"}
                    </span>
                    <h4 className="mt-1 text-lg font-bold text-gray-800 dark:text-white">
                      {item.proyecto?.nombre || "Sin Proyecto"}
                    </h4>
                  </div>
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                      Encuesta:
                    </p>
                    <p className="text-base text-gray-800 dark:text-gray-200">
                      {item.encuesta?.nombre || "Sin Nombre"}
                    </p>
                  </div>
                  <div className="mb-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                        Progreso:
                      </p>
                      <p className="text-lg font-bold text-brand-600">
                        {savedCount + pendingOfflineCount} / {assignedCount}
                      </p>
                    </div>
                    <div
                      className="h-2 w-24 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
                      role="img"
                      aria-label={`${savedCount} respuestas guardadas y ${pendingOfflineCount} pendientes offline de ${assignedCount}`}
                    >
                      <div
                        className="flex h-full"
                        style={{ width: `${savedProgress + offlineProgress}%` }}
                      >
                        <span
                          className="h-full bg-brand-500"
                          style={{ width: `${savedProgress}%` }}
                        />
                        <span
                          className="h-full bg-amber-500"
                          style={{ width: `${offlineProgress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="-mt-4 mb-5 flex justify-between text-xs">
                    <span className="text-brand-600 dark:text-brand-400">
                      {savedCount} guardadas
                    </span>
                    <span className="text-amber-600 dark:text-amber-400">
                      {pendingOfflineCount} offline
                    </span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {!isOnline ? (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          startIcon={
                            copiedId === item.id ? (
                              <Check className="size-4 text-green-500" />
                            ) : (
                              <Copy className="size-4" />
                            )
                          }
                          onClick={() =>
                            item.encuesta &&
                            handleCopyLink(item.encuesta.id, item.id)
                          }
                        >
                          {copiedId === item.id ? "Copiado" : "Copiar"}
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          className="flex-1"
                          startIcon={<ExternalLink className="size-4" />}
                          disabled={!item.encuesta?.id}
                          onClick={() => {
                            console.log(item);
                            if (!item.encuesta?.id) return;

                            const tenantId = getSessionItem("tenantId");
                            if (!tenantId) return;

                            window.open(
                              `${url_survey}${tenantId}/${item.encuesta.id}`,
                              "_blank",
                              "noopener,noreferrer",
                            );
                          }}
                        >
                          abrir
                        </Button>
                      </div>
                    ) : (
                      hasOfflineData && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full border-brand-500 text-brand-500 hover:bg-brand-50"
                          startIcon={<WifiOff className="size-4" />}
                          onClick={() => {
                            setSelectedAssignment(item);
                            setIsOfflineModalOpen(true);
                          }}
                        >
                          Responder Offline
                        </Button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <OfflineSurveyModal
        isOpen={isOfflineModalOpen}
        onClose={() => setIsOfflineModalOpen(false)}
        assignment={selectedAssignment}
        onSuccess={fetchEncuestas}
      />
    </div>
  );
}
